import { RiArrowDownLine, RiArrowUpLine, RiPulseLine, RiSubtractLine } from "@remixicon/react";
import type { MarketActivitySnapshot } from "@screener/protocol";
import { BarLineChart } from "@screener/uikit/components/bar-line-chart";
import { Button } from "@screener/uikit/components/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import { cn } from "@screener/uikit/lib/utils";
import type { StreamState } from "@/density/stream-state";
import type { StreamStore } from "@/density/use-density-stream";
import { useStreamSelector } from "@/density/use-stream-selector";
import {
  activityBarTitle,
  activityChartPoint,
  activityColorClass,
  activityMarkText,
  activityMultiplierText,
  activityTicks,
  activityTooltipHeadline,
  collectingFromText,
} from "./market-activity-label";

/**
 * Пометка активности рынка в верхней полосе (§145 пп. 1, 4–5, 7). Часть
 * экрана, не компонент UIKit: знает снимок активности из протокола. Ничего не
 * считает — показывает то, что прислал фронт.
 */

/** Рост и скругление как у пометок шапки Алертов: 26 px, radius 6. */
const MARK_CONTROL = "h-[26px] min-w-0 gap-1.5 rounded-md px-2 text-xs font-medium";

function LevelIcon({ snapshot }: { snapshot: MarketActivitySnapshot }) {
  const { state } = snapshot;
  if (state.kind !== "level") {
    return <RiPulseLine aria-hidden="true" className="size-3.5" />;
  }
  switch (state.level) {
    case "high":
      return <RiArrowUpLine aria-hidden="true" className="size-3.5" />;
    case "low":
      return <RiArrowDownLine aria-hidden="true" className="size-3.5" />;
    case "normal":
      return <RiSubtractLine aria-hidden="true" className="size-3.5" />;
  }
}

/** Сутки по часам: столбик — объём часа, линия — обычный объём. Числа считает фронт. */
function HoursChart({ snapshot }: { snapshot: MarketActivitySnapshot }) {
  const { hours } = snapshot;
  if (hours.length === 0) {
    return null;
  }
  const points = hours.map(activityChartPoint);
  return (
    <div className="flex w-full flex-col gap-1">
      <BarLineChart
        data-testid="market-activity-chart"
        points={points}
        formatTitle={(_, index) => {
          const hour = hours[index];
          return hour ? activityBarTitle(hour) : undefined;
        }}
        aria-label="Объём торгов по часам за сутки UTC"
        className="h-16"
      />
      {/* Сетка на все часы: подпись стоит в колонке своего столбика. */}
      <div
        className="grid text-[10px] text-muted-foreground tabular-nums"
        style={{ gridTemplateColumns: `repeat(${hours.length}, minmax(0, 1fr))` }}
      >
        {activityTicks(hours).map((tick) => (
          <span
            key={tick.column}
            data-tick-column={tick.column}
            className="whitespace-nowrap"
            style={{ gridColumnStart: tick.column }}
          >
            {tick.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Пометка по готовому снимку: значок и множитель ниже `md`, полный текст — выше. */
export function MarketActivityMark({ snapshot }: { snapshot: MarketActivitySnapshot }) {
  const fullText = activityMarkText(snapshot);
  const multiplier = activityMultiplierText(snapshot);
  const { state } = snapshot;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="bare"
            size="sm"
            data-market-activity=""
            className={cn(MARK_CONTROL, activityColorClass(snapshot))}
          />
        }
      >
        <LevelIcon snapshot={snapshot} />
        {/* Как у пометок Алертов: ниже `md` подпись остаётся для скринридера
            (`sr-only`), зрячему остаются значок и множитель. Не помещается на
            ширине — подпись обрезается многоточием, полный текст в подсказке. */}
        <span className="truncate max-md:sr-only md:max-w-48 xl:max-w-none">{fullText}</span>
        {multiplier === null ? null : (
          <span aria-hidden="true" className="tabular-nums md:hidden">
            {multiplier}
          </span>
        )}
      </TooltipTrigger>
      <TooltipContent className="w-72 max-w-[90vw] flex-col items-stretch gap-2">
        <p className="font-medium">{activityTooltipHeadline(snapshot)}</p>
        {state.kind === "collecting" ? <p>{collectingFromText(state.comparison_from_ms)}</p> : null}
        <HoursChart snapshot={snapshot} />
      </TooltipContent>
    </Tooltip>
  );
}

function selectMarketActivity(state: StreamState): MarketActivitySnapshot | null {
  return state.marketActivity;
}

/** Пометка шапки: читает снимок из потока. Пока снимка нет, в шапке ничего нет. */
export function MarketActivityStatus({ store }: { store: StreamStore }) {
  const snapshot = useStreamSelector(store, selectMarketActivity);
  return snapshot === null ? null : <MarketActivityMark snapshot={snapshot} />;
}
